import { Link, useLocation } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isHome = location.pathname === "/";

  const scrollToSection = (e, id) => {
    e.preventDefault();
    if (isHome) {
      const el = document.querySelector(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = "/" + id;
    }
  };

  return (
    <footer data-testid="footer" className="bg-primary text-primary-foreground">
      {/* CTA Strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-white mb-4 tracking-tight">
            Where Education Becomes Unforgettable
          </h2>
          <p className="text-base text-white/60 font-light max-w-xl mx-auto mb-8">
            Partner with Open Sky Adventure Academy and give your students experiences they will carry for a lifetime.
          </p>
          <a
            href="#inquiry"
            data-testid="footer-cta"
            onClick={(e) => scrollToSection(e, "#inquiry")}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent text-accent-foreground font-medium text-sm rounded-sm hover:bg-accent/90 transition-all hover:-translate-y-0.5"
          >
            Submit Enquiry
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="lg:col-span-1">
            <div className="mb-4">
              <span className="font-body text-xl font-semibold text-white">Open Sky</span>
              <br />
              <span className="font-body text-xs tracking-[0.15em] uppercase text-white/50">Adventure Academy</span>
            </div>
            <p className="text-sm text-white/50 font-light leading-relaxed mb-4">Explore. Build. Grow.</p>
            <p className="text-xs text-white/40 font-light">Experiential learning institution serving schools across Karnataka since over a decade.</p>
          </div>

          <div>
            <h4 className="font-body text-sm font-medium text-white uppercase tracking-wider mb-4">Programs</h4>
            <ul className="space-y-2.5">
              {["AgriLearn Campus", "AeroLab Experience", "SailScience Program"].map((p) => (
                <li key={p}>
                  <a href="#programs" onClick={(e) => scrollToSection(e, "#programs")} className="text-sm text-white/50 hover:text-white transition-colors font-light">{p}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-body text-sm font-medium text-white uppercase tracking-wider mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {[
                { label: "About Us", href: "#about" },
                { label: "Why Choose Us", href: "#why-choose" },
                { label: "Safety", href: "#safety" },
                { label: "Partners", href: "#institutions" },
              ].map((link) => (
                <li key={link.label}>
                  <a href={link.href} onClick={(e) => scrollToSection(e, link.href)} className="text-sm text-white/50 hover:text-white transition-colors font-light">{link.label}</a>
                </li>
              ))}
              <li><Link to="/blog" className="text-sm text-white/50 hover:text-white transition-colors font-light">Blog</Link></li>
              <li><Link to="/privacy" className="text-sm text-white/50 hover:text-white transition-colors font-light">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-sm text-white/50 hover:text-white transition-colors font-light">Terms & Conditions</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-body text-sm font-medium text-white uppercase tracking-wider mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                <a href="mailto:info@osaacademy.in" className="text-sm text-white/50 hover:text-white transition-colors font-light">info@osaacademy.in</a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                <div>
                  <a href="tel:+918618396055" className="text-sm text-white/50 hover:text-white transition-colors font-light block">+91 8618396055</a>
                  <a href="tel:+918147068989" className="text-sm text-white/50 hover:text-white transition-colors font-light block">+91 8147068989</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                <span className="text-sm text-white/50 font-light">A short drive from Bengaluru, Karnataka</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/30 font-light">&copy; {currentYear} Open Sky Adventure Academy. All rights reserved.</p>
          <p className="text-xs text-white/30 font-light">www.osaacademy.in</p>
        </div>
      </div>
    </footer>
  );
}
